import Tinbin from 'tinbin'
import Cookies from 'js-cookie'
import { createHashHistory as createHistory } from 'history'

import N from '../utils/assert-never'
import { create as createStore } from '../utils/store'
import HttpFactory, { interceptors } from '../utils/http'
import { getConfigParam } from '../utils/config'

import reducer, { listenerMiddleware } from '../model'
import { showNotification } from '../model/ui/actions'
import { Notification } from '../components/NotificationContainer'

// Service factories
import ProjectServiceFactoryMock from '../services/mock/projects'
import ProjectServiceFactoryApi from '../services/api/projects'

import CreativeServiceFactoryMock from '../services/mock/creatives'
import CreativeServiceFactoryApi from '../services/api/creatives'

import BlueprintServiceFactoryMock from '../services/mock/blueprints'
import BlueprintServiceFactoryApi from '../services/api/blueprints'

import FilesServiceFactoryMock from '../services/mock/files'
import FilesServiceFactoryApi from '../services/api/files'

import UserServiceFactorMock from '../services/mock/user'
import UserServiceFactoryApi from '../services/api/user'

export type AppEnv = 'mock' | 'api' | 'test'

const services = async (DI: any, env: AppEnv) => {
  // First fill in services, so that store can rely on services
  if (env === 'mock' || env === 'test') {
    DI.put('ProjectService', ProjectServiceFactoryMock(DI))
    DI.put('CreativeService', CreativeServiceFactoryMock(DI))
    DI.put('BlueprintService', BlueprintServiceFactoryMock(DI))
    DI.put('FilesService', FilesServiceFactoryMock(DI))
    DI.put('UserService', UserServiceFactorMock(DI))
  } else if (env === 'api') {
    DI.put('ProjectService', ProjectServiceFactoryApi(DI))
    DI.put('CreativeService', CreativeServiceFactoryApi(DI))
    DI.put('BlueprintService', BlueprintServiceFactoryApi(DI))
    DI.put('FilesService', FilesServiceFactoryApi(DI))
    DI.put('UserService', UserServiceFactoryApi(DI))
  } else {
    return N(env)
  }
}

const setupStore = async (DI: any) => {
  const initialState = {}
  const store = createStore(
    reducer,
    initialState,
    process.env.NODE_ENV || 'development',
    [listenerMiddleware.middleware],
  )

  DI.put('store', store)
  DI.put('showNotification', (notification: Notification) => {
    store.dispatch(showNotification(notification))
  })

  return DI
}

const http = async (DI: any) => {
  const store = DI.get('store')

  const http = ({ expectedErrorCodes = [] } = {}) => {
    return HttpFactory({
      baseURL: getConfigParam('REACT_APP_API_URL'),
      withCredentials: true,
      interceptors: [
        interceptors.data({}),
        interceptors.auth({
          store,
          Action: () => ({ type: '/auth/UNAUTHORIZED' }),
        }),
        interceptors.apiError({ store, expectedErrorCodes }),
        interceptors.apiWarnings({}),
      ],
    })
  }

  DI.put('http', http)

  return DI
}

export function getLocale() {
  const locale = Cookies.get('user_lang') || 'de'

  return locale.substring(0, 2)
}

export const initialize = async (DI: any, env: AppEnv) => {
  DI.put('history', createHistory())

  await services(DI, env)
  await setupStore(DI)
  await http(DI)

  DI.put('locale', getLocale())

  return DI
}

export default Tinbin()
